import { useAuth } from "@/components/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Pixel } from "./types";
import { PixelCard } from "./PixelCard";

interface PixelListProps {
  pixels: Pixel[];
  selectedPixel: Pixel | null;
  onPixelSelect: (pixel: Pixel) => void;
  onPixelEdit: (pixel: Pixel) => void;
  onPixelDelete: (pixel: Pixel) => void;
}

export const PixelList = ({
  pixels,
  selectedPixel,
  onPixelSelect,
  onPixelEdit,
  onPixelDelete
}: PixelListProps) => {
  const { user } = useAuth();
  
  // Query to get organizations created by the current user
  const { data: ownedOrgs } = useQuery({
    queryKey: ['owned-organizations', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('organizations')
        .select('id')
        .eq('created_by', user.id);
        
      if (error) {
        console.error("Error fetching owned organizations:", error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!user?.id,
  });
  
  const ownedOrgIds = new Set((ownedOrgs || []).map(org => org.id));
  
  const canDeletePixel = (pixel: Pixel) => {
    // Case 1: User's own pixel without organization association
    if (!pixel.organization_id && pixel.user_id === user?.id) {
      return true;
    }
    
    // Case 2: Pixel belongs to an organization owned by the user
    if (pixel.organization_id && ownedOrgIds.has(pixel.organization_id)) {
      return true;
    }
    
    // Otherwise, no delete permission
    return false;
  };
  
  return (
    <div className="mt-4 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
      {pixels.map((pixel) => (
        <PixelCard
          key={pixel.id}
          pixel={pixel}
          isSelected={selectedPixel?.id === pixel.id}
          onSelect={onPixelSelect}
          onEdit={onPixelEdit}
          onDelete={onPixelDelete}
          canDelete={canDeletePixel(pixel)}
        />
      ))}
    </div>
  );
};
